import * as React from 'react';
import { queryFooter, queryNavbar, queryPageBySlug, querySectionsBySystemIds } from '@/clients/queries';
import { chunk } from '@/utils/arrays';
import config from '@/config/config';
import { FooterQuery, NavbarQuery, PageBySlugQuery, PageSectionsBySystemIdQuery } from 'types/generated';
import PageContainer from '@/components/PageContainer';
import { getOr } from '@/utils/options';
import Roaming from '@/components/Roaming';
import { GetStaticProps } from 'next';
import { centeredMainGridClasses } from '@/styles/grid';
import { Label } from 'components/src/typography';
import 'flag-icons/css/flag-icons.min.css';
import '@arcgis/core/assets/esri/themes/light/main.css';
import { CountryData } from '@/components/Roaming/types';
import { apolloClient, getClient } from '@/utils/contentful';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { RadioSchedule } from '@/models/RadioTypes';
import { Log } from '@/logging/datadog';

type Props = {
  page: PageBySlugQuery;
  navbar: NavbarQuery;
  footer: FooterQuery;
  sections: Array<PageSectionsBySystemIdQuery>;
  radioSchedules: Record<string, RadioSchedule[]>;
  orderedIds: Array<string>;
  reikiCountries: Array<CountryData>;
};

const ReikiPage = ({ navbar, footer, page, sections, radioSchedules, orderedIds, reikiCountries }: Props) => {
  const pageCollection = page.pageCollection?.items[0];

  if (!pageCollection) {
    return (
      <div className={`${centeredMainGridClasses} mt-16 text-center`}>
        <Label>{'404 - /reiki page not found'}</Label>
      </div>
    );
  }

  return (
    <PageContainer
      title={getOr(pageCollection.title, 'Sýn')}
      metaTags={pageCollection.metaTags}
      contactFormSetting={pageCollection.contactFormSetting}
      navbar={navbar}
      footer={footer}
      radioSchedules={radioSchedules}
    >
      <Roaming sections={sections} orderedIds={orderedIds} reikiCountries={reikiCountries} />
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = 'reiki';
  const inPreview = getOr(context.draftMode, false);
  try {
    const client = getClient(inPreview);

    const contentfulPage = (await queryPageBySlug(client, slug, inPreview)).data;

    if (!contentfulPage.pageCollection) {
      Log.error(`no page collection found in graphql response when fetching page details for ${slug}`);
      return {
        notFound: true,
      };
    }

    if (!contentfulPage.pageCollection.items) {
      Log.error(`no items array set in page details for ${slug}`);
      return {
        notFound: true,
      };
    }

    const page = contentfulPage.pageCollection.items[0] || undefined;

    if (!page) {
      Log.error(`no page component found in graphql response for slug ${slug}`);
      return {
        notFound: true,
      };
    }

    const sectionIds = (page.sectionsCollection?.items.map((x) => x?.sys.id) || []) as string[];

    const chunkedSections = chunk(sectionIds, config.SECTION_CHUNK_SIZE).map((c) => {
      return querySectionsBySystemIds(client, c, inPreview).then((x) => x.data);
    });

    const auth = btoa(process.env.NEXT_PUBLIC_API_USER_NAME + ':' + process.env.NEXT_PUBLIC_API_PASSWORD);
    const reikiResponse = await fetch(
      `${process.env.NEXT_PUBLIC_VODAFONE_EXPERIENCE_ENDPOINT}/roaming/countries/list` || '',
      {
        method: 'GET',
        headers: {
          Authorization: `Basic ${auth}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const reikiCountries: Array<CountryData> = await reikiResponse.json();

    if (!reikiResponse || !reikiCountries) {
      Log.error(`no country data found in reiki response or response was not ok`);
      return {
        notFound: true,
      };
    }

    return {
      props: {
        navbar: (await queryNavbar(apolloClient)).data,
        footer: (await queryFooter(apolloClient)).data,
        page: contentfulPage,
        sections: await Promise.all(chunkedSections),
        radioSchedules: await getRadioSchedules(),
        orderedIds: sectionIds,
        reikiCountries,
      },
      // Make sure to rebuild page every 12 hours to have the latest reiki data
      revalidate: 60 * 60 * 12,
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);
    throw err;
  }
};

export default ReikiPage;
